import { useState, useMemo, useEffect, useCallback } from "react";
import { StableMasonry } from "./StableMasonry";
import { useArtists, useArtist } from "@/hooks";
import { useUIStore } from "@/stores/uiStore";
import { AnimatedImage } from "./AnimatedImage";
import { Lightbox } from "./Lightbox";
import { FilterBar } from "./FilterBar";
import { ActiveFilters } from "./ActiveFilters";
import type { ImageGalleryProps, GalleryImageWithArtist, GalleryImage, ContentRating } from "./types";

// Round-robin interleave across artists so the top rows show variety rather
// than being dominated by one artist's burst upload. Order within each
// artist is preserved (caller sorts by created_at DESC), and artists
// themselves are ordered by their most-recent upload — so the very first
// tile is still the newest image overall, but the next tiles show different
// artists before going to the same artist's second-most-recent work.
function interleaveByArtist(
  items: GalleryImageWithArtist[]
): GalleryImageWithArtist[] {
  if (items.length <= 1) return items;
  const byArtist = new Map<string, GalleryImageWithArtist[]>();
  for (const item of items) {
    const list = byArtist.get(item.artist.id);
    if (list) list.push(item);
    else byArtist.set(item.artist.id, [item]);
  }
  const artistOrder = [...byArtist.entries()]
    .sort(([, a], [, b]) => {
      const da = new Date(a[0].image.created_at || 0).getTime();
      const db = new Date(b[0].image.created_at || 0).getTime();
      return db - da;
    })
    .map(([id]) => id);
  const out: GalleryImageWithArtist[] = [];
  let round = 0;
  while (out.length < items.length) {
    let pushed = false;
    for (const id of artistOrder) {
      const list = byArtist.get(id)!;
      if (round < list.length) {
        out.push(list[round]);
        pushed = true;
      }
    }
    if (!pushed) break;
    round++;
  }
  return out;
}

const IMAGES_PER_PAGE = 25;

export function ImageGallery({
  artistSlug,
  selectedTag,
  showFilters = true,
  showTitle = true,
  title = "Artist Portfolio",
  subtitle = "Click on any image to view full size",
  onClearParentTag,
}: ImageGalleryProps) {
  const { data: allArtists } = useArtists();
  const { data: singleArtist, isLoading: isLoadingSingleArtist } = useArtist(artistSlug || "");
  const { selectedContentRatings } = useUIStore();

  const [visibleCount, setVisibleCount] = useState(IMAGES_PER_PAGE);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [selectedArtistIds, setSelectedArtistIds] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // When artistSlug is provided, only show that artist's work
  // Don't fall back to allArtists while loading - wait for the single artist data
  const artists = artistSlug
    ? (singleArtist ? [singleArtist] : undefined)
    : allArtists;

  // Normalize during aggregation so "Shading" and "shading" don't render as
  // two separate filter chips (fix for M-FILT-2).
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    artists?.forEach((artist) => {
      artist.portfolio?.forEach((img) => {
        img.tags?.forEach((tag: string) => {
          const normalized = tag.trim().toLowerCase();
          if (normalized) tags.add(normalized);
        });
      });
    });
    return Array.from(tags).sort();
  }, [artists]);

  const sortedAndFilteredImages = useMemo(() => {
    const allImages: GalleryImageWithArtist[] = [];

    artists?.forEach((artist) => {
      artist.portfolio?.forEach((img) => {
        const galleryImage: GalleryImage = {
          id: img.id,
          url: img.url,
          thumbnail: img.thumbnail,
          altText: img.altText,
          tags: img.tags,
          contentRating: img.contentRating as ContentRating,
          created_at: img.uploadedAt,
          width: img.width ?? undefined,
          height: img.height ?? undefined,
        };
        allImages.push({ image: galleryImage, artist });
      });
    });

    allImages.sort((a, b) => {
      const dateA = new Date(a.image.created_at || 0).getTime();
      const dateB = new Date(b.image.created_at || 0).getTime();
      return dateB - dateA;
    });

    const filtered = allImages.filter(({ image, artist }) => {
      if (!selectedContentRatings.includes(image.contentRating)) {
        return false;
      }

      if (selectedArtistIds.length > 0 && !selectedArtistIds.includes(artist.id)) {
        return false;
      }

      // Case-insensitive tag comparison (fix for H-FILT-1). DB stores tags in
      // mixed case but ServicesAccordion emits lowercase; comparing raw fails.
      const effectiveTags = selectedTag ? [...selectedTags, selectedTag] : selectedTags;
      if (effectiveTags.length > 0) {
        const normalizedImageTags = (image.tags || []).map((t) => t.trim().toLowerCase());
        const hasMatchingTag = effectiveTags.some((tag) => {
          const needle = tag.trim().toLowerCase();
          return normalizedImageTags.includes(needle);
        });
        if (!hasMatchingTag) return false;
      }

      return true;
    });

    // Round-robin interleave by artist for Pinterest-style variety.
    // When viewing a single artist's gallery, interleaving is a no-op.
    return interleaveByArtist(filtered);
  }, [artists, selectedContentRatings, selectedArtistIds, selectedTags, selectedTag]);

  // Reset pagination when filter criteria change so "Load more" restarts
  // from page 1 with newly filtered results (fix for L-FILT-4).
  useEffect(() => {
    setVisibleCount(IMAGES_PER_PAGE);
  }, [selectedTag, selectedTags, selectedArtistIds, selectedContentRatings]);

  const visibleImages = sortedAndFilteredImages.slice(0, visibleCount);
  const hasMore = visibleCount < sortedAndFilteredImages.length;
  const remainingCount = sortedAndFilteredImages.length - visibleCount;

  const handleClearFilters = () => {
    setSelectedArtistIds([]);
    setSelectedTags([]);
    // Also clear the parent-owned tag if the parent opted in (fix for M-FILT-3).
    onClearParentTag?.();
  };

  const handleRemoveArtist = (id: string) => {
    setSelectedArtistIds((prev) => prev.filter((a) => a !== id));
  };

  const handleRemoveTag = (tag: string) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + IMAGES_PER_PAGE);
  };

  // Stable reference so StableMasonry's useMemo doesn't thrash.
  const getImageAspectRatio = useCallback((entry: GalleryImageWithArtist) => {
    const w = entry.image.width;
    const h = entry.image.height;
    return w && h ? h / w : 1;
  }, []);

  // Show loading state when waiting for artist data
  if (artistSlug && isLoadingSingleArtist) {
    return (
      <section id="portfolio" className="py-16 bg-[var(--color-bg-light)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <p className="text-[var(--color-text-muted)]">Loading gallery...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!artists) return null;

  return (
    <section id="portfolio" className="py-16 bg-[var(--color-bg-light)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showTitle && (
          <>
            <h2 className="font-[var(--font-display)] text-3xl text-[var(--color-text)] text-center mb-2">
              {title.includes(" ") ? (
                <>
                  {title.split(" ")[0]}{" "}
                  <em className="text-[var(--color-primary)]">{title.split(" ").slice(1).join(" ")}</em>
                </>
              ) : (
                title
              )}
            </h2>
            <p className="text-center text-[var(--color-text-muted)] mb-8">
              {subtitle}
            </p>
          </>
        )}

        {showFilters && !artistSlug && (
          <FilterBar
            artists={artists}
            allTags={allTags}
            selectedArtistIds={selectedArtistIds}
            selectedTags={selectedTags}
            onArtistsChange={setSelectedArtistIds}
            onTagsChange={setSelectedTags}
            onClear={handleClearFilters}
          />
        )}

        <ActiveFilters
          artists={artists}
          selectedArtistIds={selectedArtistIds}
          selectedTags={selectedTags}
          selectedTag={selectedTag}
          onRemoveArtist={handleRemoveArtist}
          onRemoveTag={handleRemoveTag}
          onClear={handleClearFilters}
          onClearParentTag={onClearParentTag}
        />

        {/* StableMasonry: shortest-column-first packing. Once an item is
            placed in a column, it stays there — Load More appends items to
            whichever column is currently shortest without moving existing
            items (unlike CSS `columns` and react-masonry-css). */}
        {visibleImages.length > 0 ? (
          <StableMasonry
            items={visibleImages}
            getAspectRatio={getImageAspectRatio}
            renderItem={(entry, originalIndex) => (
              <AnimatedImage
                image={entry.image}
                artist={entry.artist}
                onClick={() => setLightboxIndex(originalIndex)}
              />
            )}
          />
        ) : (
          <div className="text-center py-20">
            <p className="text-[var(--color-text-muted)]">
              No images found with the current filters.
            </p>
          </div>
        )}

        {/* Load More Button - simple, matches live site */}
        {hasMore && (
          <div className="text-center mt-10">
            <button
              onClick={handleLoadMore}
              className="px-8 py-3 rounded-full bg-[var(--color-bg-card)] border border-[var(--color-border)] text-[var(--color-text)] font-medium hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all duration-200"
            >
              Load More
            </button>
            {remainingCount > 0 && (
              <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                {remainingCount} more {remainingCount === 1 ? "image" : "images"}
              </p>
            )}
          </div>
        )}
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={sortedAndFilteredImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </section>
  );
}
